import { Link, Outlet, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { AppSidebar } from '@/app/AppSidebar';
import {
  activeNavItem,
  readNavOrigin,
  scopeNavItems,
  visibleNavItems,
} from '@/shared/constants/navigation';
import { NavGlyph } from '@/app/NavGlyph';
import { useAuth } from '@/app/providers/AuthProvider';
import { useNavIndicator } from '@/app/useNavIndicator';
import { UserMenu } from '@/app/UserMenu';
import { signOut } from '@/features/auth';
import { useMyScopeBindings } from '@/features/org';
import { useReviewQueue } from '@/features/review';
import { AppShell } from '@/shared/components/AppShell';
import { can, ROLE_LABELS } from '@/shared/constants/roles';
import { cn } from '@/shared/lib/utils';

/**
 * Moldura das rotas autenticadas: monta `sidebar` e `topbar` para o `AppShell`
 * (dumb, em `shared/`) a partir de quem está logado — é aqui, e só aqui, que
 * sessão vira interface (AppShell nunca importa de `features/`, ADR-006).
 */
export function AppLayout() {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const indicator = useNavIndicator<HTMLElement>('horizontal');
  const { pathname, state } = useLocation();

  // Calculado antes do guard de baixo, porque hook não pode ser condicional
  // (regra dos hooks) — `useReviewQueue` sempre é chamado, e é o `enabled`
  // interno que decide se a consulta dispara.
  const isReviewer = Boolean(currentUser && can(currentUser.roles, 'reviewProject'));
  const reviewQueueQuery = useReviewQueue(isReviewer);
  // A seção "Minha área" (ADR-012 regra 6). Sem `can()` nenhum: quem tem item é
  // quem tem vínculo, e é a consulta que responde isso — o `enabled` interno
  // segura enquanto não há perfil.
  const scopeBindingsQuery = useMyScopeBindings(currentUser?.profile.id);

  async function handleSignOut() {
    await signOut();
    // Computador compartilhado (secretaria): não deixar cache da pessoa
    // anterior visível para quem logar em seguida na mesma aba.
    queryClient.clear();
  }

  // RequireAuth garante currentUser !== null/undefined antes de renderizar
  // este layout — o guard abaixo é só para o TypeScript.
  if (!currentUser) return null;

  const fullName = currentUser.profile.full_name;
  const roleLabel = currentUser.roles.map((role) => ROLE_LABELS[role]).join(' · ');
  /**
   * A navegação inteira, montada **uma vez só** e entregue pronta às duas
   * barras — a coluna e a fileira do celular (ADR-012 regra 6).
   *
   * A seção de vínculos entra depois dos estáticos porque `groupBySection`
   * respeita a ordem de chegada, e "Minha área" tem de vir antes de "Seu
   * trabalho": lugar antes de fila, como o resto da barra já faz.
   *
   * Enquanto a consulta não volta, a lista é só a estática e a seção aparece
   * logo em seguida. É a troca certa: a alternativa seria segurar a barra
   * inteira esperando uma consulta de três linhas, e uma moldura que só aparece
   * depois do conteúdo é pior que uma seção que chega um instante depois.
   */
  const navItems = [
    ...visibleNavItems(currentUser.roles),
    ...scopeNavItems(scopeBindingsQuery.data ?? []),
  ];
  // Mesma resolução da sidebar, pelo mesmo motivo do comentário lá: as duas
  // navegações não podem discordar sobre onde a pessoa está.
  const activeItem = activeNavItem(navItems, pathname, readNavOrigin(state));
  // Mesma chave de `queryKeys.reviewQueue` que `/analise` consome (§6.1): uma
  // requisição, cache compartilhado. `undefined` enquanto carrega — nunca um
  // "0" que ainda não é verdade.
  const reviewQueueCount = isReviewer ? reviewQueueQuery.data?.length : undefined;

  /**
   * O Calendário é a única rota de altura cheia (spec-calendar-ux.md §6.2): a
   * grade precisa de altura DEFINIDA para que a célula tenha tamanho fixo, e
   * numa página que rola ela não tem de quem herdá-la.
   *
   * A decisão mora aqui, e não no `AppShell`: `shared/` não conhece rota nenhuma
   * (ADR-006), e é `app/` que sabe o que está montado. Uma rota a mais é uma
   * linha nesta comparação.
   */
  const isFullHeightRoute = pathname === '/calendario';

  return (
    <AppShell
      fullHeight={isFullHeightRoute}
      sidebar={
        <AppSidebar
          fullName={fullName}
          roleLabel={roleLabel}
          items={navItems}
          reviewQueueCount={reviewQueueCount}
          onSignOut={() => void handleSignOut()}
        />
      }
      topbar={
        /* Abaixo de 768px a sidebar sai do fluxo — e leva junto a navegação e
           o menu da pessoa. Os dois reaparecem aqui, em ícones; sem isto não
           haveria como sair do sistema pelo celular. */
        <div className="flex min-w-0 items-center gap-1 md:hidden">
          <nav
            ref={indicator.containerRef}
            /**
             * **Rola na horizontal, e o `UserMenu` ao lado é a razão.** A fileira
             * era de três a quatro ícones fixos; com um item por vínculo ela
             * passou a depender do cadastro — quem lidera três ministérios não
             * cabe nos 375px. Sem `min-w-0` + rolagem, o que o `flex` empurra
             * para fora da tela é justamente o menu da pessoa, e sem ele não há
             * como sair do sistema pelo celular.
             *
             * `ibft-strip` (styles/index.css) porque a barra de rolagem nativa do Android
             * ocuparia a metade de baixo de uma fileira de 36px.
             */
            className="ibft-strip relative flex min-w-0 items-center gap-1 overflow-x-auto"
            aria-label="Navegação principal"
          >
            {/* A mesma marca da sidebar, deitada: aqui os três itens são abas
                lado a lado, e ela escorrega na horizontal entre eles. */}
            {indicator.style ? (
              <span
                aria-hidden="true"
                style={indicator.style}
                className="absolute top-0 h-9 rounded-md bg-primary/10 transition-[left,width] duration-base ease-out"
              />
            ) : null}

            {/* Mesma decisão de item ativo da sidebar, pelo mesmo motivo —
                as duas navegações não podem discordar sobre onde a pessoa
                está só porque a tela mudou de largura. */}
            {navItems.map((item) => {
              const isActive = activeItem?.to === item.to;

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    // `shrink-0`: dentro de um contêiner que rola, o padrão do
                    // flex é espremer os itens até o ícone deformar.
                    'relative flex size-9 shrink-0 items-center justify-center rounded-md',
                    'transition-[color,background-color,transform] duration-fast ease-out active:scale-90',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                    isActive
                      ? 'text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  )}
                >
                  <NavGlyph icon={item.icon} />
                  <span className="sr-only">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <UserMenu
            fullName={fullName}
            roleLabel={roleLabel}
            collapsed
            direction="down"
            onSignOut={() => void handleSignOut()}
          />
        </div>
      }
    >
      <Outlet />
    </AppShell>
  );
}
